import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { isCancellation } from '../utils/requestLifecycle';
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Tune as VariableIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';

// In-memory cache keyed by (variable.query, dataSource, timeRange) so opening
// the same dashboard twice doesn't re-run the option-discovery query.
//
// Entries expire after OPTIONS_TTL_MS (PLAN G3). Without a bound, the option list a session first
// saw was the one it kept: a host that appeared later never reached the dropdown until a reload.
// Five minutes matches how often a new label value is worth surfacing, and is far longer than any
// back-and-forth between dashboards. Expired entries are dropped on read, and the map is capped so
// a long session cannot grow it without bound.
export const OPTIONS_TTL_MS = 5 * 60 * 1000;
const OPTIONS_CACHE_MAX = 500;
const queryOptionsCache = new Map(); // key -> { result, at }

const cacheGet = (key, now = Date.now()) => {
  const hit = queryOptionsCache.get(key);
  if (!hit) return undefined;
  if (now - hit.at >= OPTIONS_TTL_MS) {
    queryOptionsCache.delete(key);
    return undefined;
  }
  return hit.result;
};

const cacheSet = (key, result, now = Date.now()) => {
  queryOptionsCache.delete(key);
  queryOptionsCache.set(key, { result, at: now });
  // Map iteration is insertion order, so the first key is the oldest write.
  while (queryOptionsCache.size > OPTIONS_CACHE_MAX) {
    queryOptionsCache.delete(queryOptionsCache.keys().next().value);
  }
};

/**
 * The variable names a query references via {@code $name} / {@code ${name}}
 * (excluding the {@code $__} SQL macros). These are the *other* variables whose
 * selected values feed this query — the basis for cascading/dependent variables.
 */
const referencedVarNames = (query) => {
  const names = new Set();
  if (!query) return names;
  const re = /\$\{(\w+)\}|\$(\w+)/g;
  let m;
  while ((m = re.exec(query)) !== null) {
    const n = m[1] || m[2];
    if (n && !n.startsWith('__')) names.add(n);
  }
  return names;
};

/**
 * For variables of type 'query', resolve the dropdown options by running the
 * variable's SQL via the dashboards endpoint. Returns the first non-numeric
 * column's distinct values.
 *
 * {@code values} carries the currently-selected values of the *other* variables
 * so a dependent variable's query can reference them (cascading variables); they
 * are sent as {@code variables} and substituted server-side, exactly like widget
 * queries.
 */
export const resolveQueryOptions = async ({ query, dataSource = 'clickhouse_metrics', timeRange = '1h', values: varValues = {}, signal }) => {
  if (!query || !query.trim()) return [];
  // Cache per (dataSource, timeRange, query, the upstream values this query
  // actually references). Scoping the key to referenced values means an
  // unrelated variable change still hits the cache instead of re-querying.
  const refdValues = {};
  referencedVarNames(query).forEach(n => { if (varValues[n] !== undefined) refdValues[n] = varValues[n]; });
  const key = `${dataSource}::${timeRange}::${query}::${JSON.stringify(refdValues)}`;
  const cached = cacheGet(key);
  if (cached !== undefined) return cached;
  const tok = (typeof localStorage !== 'undefined') ? localStorage.getItem('authToken') : null;
  const headers = { 'Content-Type': 'application/json', ...(tok && { Authorization: `Bearer ${tok}` }) };
  try {
    // Only the variables this query references are sent, as for widgets: the rest cannot change
    // its answer, and sending them would split the server's cache for nothing.
    // `purpose: 'variable'` puts the option query in its own admission lane on the server, so the
    // widgets it feeds cannot starve it, and lets the server keep its options for a few minutes.
    const res = await axios.post('/api/metrics/sql-query',
      { query, dataSource, timeRange, variables: refdValues, purpose: 'variable' }, { headers, signal });
    const data = res.data;
    // Surface backend rejections (table not whitelisted, SQL error, …) instead
    // of silently showing an empty dropdown — "works in the explorer, empty in
    // the filter" was undiagnosable without this.
    if (data?.status === 'error' || data?.error) {
      return { opts: [], error: String(data.error || 'query failed') };
    }
    let values = [];
    if (data?.type === 'table' && Array.isArray(data.rows)) {
      // Pick the first column that exists across rows
      const cols = data.columns || (data.rows[0] ? Object.keys(data.rows[0]) : []);
      const col = cols[0];
      values = data.rows.map(r => r[col]).filter(v => v != null && v !== '');
    } else if (data?.type === 'scalar' && data.row) {
      const first = Object.values(data.row)[0];
      if (first != null) values = [first];
    } else if (data?.type === 'timeseries' && Array.isArray(data.data)) {
      // For timeseries shape, take the metric labels' first non-__name__ value
      values = data.data
        .map(s => Object.entries(s.metric || {}).find(([k]) => !k.startsWith('__'))?.[1])
        .filter(Boolean);
    } else {
      return { opts: [], error: 'query returned an unsupported shape — return plain rows (first column = the option values)' };
    }
    // Dedup, stringify, drop blanks
    const seen = new Set();
    const opts = values
      .map(v => String(v))
      .filter(v => v.length > 0 && !seen.has(v) && (seen.add(v), true))
      .map(v => ({ value: v, label: v }));
    const result = { opts, error: values.length === 0 ? 'query returned no values in the dashboard time range' : null };
    cacheSet(key, result);
    return result;
  } catch (e) {
    // A superseded resolution (the time range or an upstream value changed) is not an error,
    // and must not be cached or shown as one.
    if (isCancellation(e)) return { opts: [], error: null, cancelled: true };
    return { opts: [], error: e?.response?.data?.error || e?.message || 'query failed' };
  }
};

export const clearVariableQueryCache = () => queryOptionsCache.clear();

/**
 * Ensure the current value(s) appear in the option list so a Select never goes
 * blank when cascading re-resolves options that no longer contain the current
 * selection (and so curated/wildcard values like "%" survive discovery).
 */
const withCurrentValue = (opts, current) => {
  if (current == null || current === '') return opts;
  const vals = Array.isArray(current) ? current : [current];
  const present = new Set((opts || []).map(o => String(o.value)));
  const extra = vals
    .filter(x => x !== '' && x != null && !present.has(String(x)))
    .map(x => ({ value: x, label: String(x) }));
  return extra.length ? [...opts, ...extra] : opts;
};

/**
 * The variable's default (typically the '%' wildcard meaning "everything") is
 * a PERMANENT option pinned at the top of the list. It used to survive only
 * while it WAS the current value — pick a concrete host once and the '%'
 * entry vanished with no way back to "all".
 */
const withDefaultAndCurrent = (opts, defaultValue, current) => {
  let out = opts || [];
  if (defaultValue != null && defaultValue !== ''
      && !out.some(o => String(o.value) === String(defaultValue))) {
    const label = String(defaultValue) === '%' ? 'All' : String(defaultValue);
    out = [{ value: defaultValue, label }, ...out];
  }
  return withCurrentValue(out, current);
};

/**
 * Renders the live variable bar at the top of the dashboard viewer.
 * For each declared variable, exposes a small input the operator uses to
 * filter every widget on the dashboard simultaneously. Variable values
 * are sent as the {@code variables} field of the SQL query DTO so the
 * backend substitutes {@code $name} / {@code ${name}} into the widget
 * queries.
 */
export const DashboardVariableBar = ({ variables = [], values = {}, onChange, timeRange }) => {
  // Resolve query-driven option lists, merged onto the variable definitions
  // before render. Resolution runs in declared order and feeds each query the
  // current values so a later variable's options can depend on an earlier
  // variable's selection (cascading variables). Re-runs whenever a value
  // changes so dependent dropdowns refresh.
  const [resolvedOptions, setResolvedOptions] = useState({});
  const [resolveErrors, setResolveErrors] = useState({});
  const valuesKey = JSON.stringify(values || {});
  useEffect(() => {
    let cancelled = false;
    // Superseded option queries are aborted, not left to finish (PLAN G3): a time-range or
    // upstream-value change makes the in-flight answer useless, and the server is still running it.
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const queryVars = (variables || []).filter(v => v.type === 'query' && v.query);
    if (queryVars.length === 0) { setResolvedOptions({}); setResolveErrors({}); return undefined; }
    (async () => {
      const resolved = {};
      const errors = {};
      // Sequential (not Promise.all) so a dependent variable resolves against
      // the same value snapshot that already holds the upstream selections.
      for (const v of queryVars) {
        const { opts, error } = await resolveQueryOptions({
          query: v.query, dataSource: v.dataSource, timeRange, values, signal: controller?.signal,
        });
        if (cancelled) return;
        resolved[v.name] = opts;
        if (error) errors[v.name] = error;
      }
      if (!cancelled) { setResolvedOptions(resolved); setResolveErrors(errors); }
    })();
    return () => {
      cancelled = true;
      try { controller?.abort(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, timeRange, valuesKey]);

  if (!variables || variables.length === 0) return null;

  const set = (name, v) => onChange?.({ ...values, [name]: v });

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 2, py: 1.5,
        borderBottom: '1px solid',
        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'divider',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(5, 5, 5, 0.6)' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(16px)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexWrap: 'wrap',
        rowGap: 1,
      }}
    >
      <VariableIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      {variables.map((v) => {
        // For query-type variables, use the resolved options. Fall back to
        // the static options if the query hasn't returned yet. Always keep the
        // current value selectable — when cascading changes the option list the
        // selection may no longer be present, and we don't want the dropdown to
        // blank out or drop a wildcard default like "%".
        const merged = v.type === 'query' && resolvedOptions[v.name]
          ? { ...v, type: 'select', options: withDefaultAndCurrent(resolvedOptions[v.name], v.defaultValue, values[v.name]) }
          : v;
        return (
          <Box key={v.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {v.label || v.name}
            </Typography>
            {renderInput(merged, values[v.name] ?? v.defaultValue ?? '', (val) => set(v.name, val))}
            {resolveErrors[v.name] && (
              <Tooltip title={`Options query: ${resolveErrors[v.name]}`}>
                <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const renderInput = (v, value, onChange) => {
  if (v.type === 'select') {
    const opts = parseOptions(v.options);
    if (v.multiValue) {
      const current = Array.isArray(value) ? value : (value ? String(value).split(',').filter(Boolean) : []);
      return (
        <Select
          size="small" multiple displayEmpty
          value={current}
          onChange={(e) => onChange(e.target.value)}
          renderValue={(arr) =>
            arr.length === 0 ? <em style={{ opacity: 0.5 }}>any</em> :
            (arr.length <= 2 ? arr.join(', ') : `${arr.length} selected`)
          }
          sx={{ minWidth: 140, fontSize: 13 }}
        >
          {opts.map(o => <MenuItem key={o.value} value={o.value} dense>{o.label}</MenuItem>)}
        </Select>
      );
    }
    return (
      <Select
        size="small" displayEmpty
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        sx={{ minWidth: 140, fontSize: 13 }}
      >
        {opts.map(o => <MenuItem key={o.value} value={o.value} dense>{o.label}</MenuItem>)}
      </Select>
    );
  }
  return (
    <TextField
      size="small"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 140, '& input': { fontSize: 13, py: 0.5 } }}
    />
  );
};

const parseOptions = (opts) => {
  if (!opts) return [];
  if (Array.isArray(opts)) {
    return opts.map(o => typeof o === 'string' ? { value: o, label: o } : o);
  }
  if (typeof opts === 'string') {
    return opts.split(',').map(s => s.trim()).filter(Boolean).map(s => ({ value: s, label: s }));
  }
  return [];
};

/**
 * Variable editor used inside the dashboard settings drawer of the designer.
 * Adds/removes/edits the variable definitions persisted on the dashboard.
 */
export const DashboardVariableEditor = ({ variables = [], onChange }) => {
  const [draft, setDraft] = useState(variables);
  useEffect(() => { setDraft(variables); }, [variables]);

  const save = (next) => { setDraft(next); onChange?.(next); };
  const update = (i, patch) => save(draft.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const remove = (i) => save(draft.filter((_, idx) => idx !== i));
  const add = () => save([...draft, { name: 'new_var', label: '', type: 'text', defaultValue: '', options: [], multiValue: false }]);

  return (
    <Stack spacing={1.5} sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>Dashboard variables</Typography>
        <Chip label="+ Add variable" size="small" onClick={add} clickable color="primary" variant="outlined" />
      </Stack>

      {draft.length === 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Variables let widgets share filters. Reference them in any SQL as <code>$name</code> or <code>${'${name}'}</code>.
        </Typography>
      )}

      {draft.map((v, i) => (
        <Box key={i} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small" label="Name" value={v.name}
              onChange={(e) => update(i, { name: e.target.value.replace(/[^\w]/g, '') })}
              sx={{ flex: 1 }}
              helperText="No spaces; use letters, digits, _"
            />
            <TextField
              size="small" label="Display label" value={v.label || ''}
              onChange={(e) => update(i, { label: e.target.value })}
              sx={{ flex: 1 }}
            />
            <Select
              size="small" value={v.type || 'text'}
              onChange={(e) => update(i, { type: e.target.value })}
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="select">Select</MenuItem>
              <MenuItem value="query">Query</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small" label="Default value" value={v.defaultValue || ''}
              onChange={(e) => update(i, { defaultValue: e.target.value })}
              sx={{ flex: 1 }}
            />
            {v.type === 'select' && (
              <TextField
                size="small" label="Options (comma-separated)"
                value={Array.isArray(v.options) ? v.options.join(',') : (v.options || '')}
                onChange={(e) => update(i, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                sx={{ flex: 2 }}
              />
            )}
            {v.type === 'query' && (
              <TextField
                size="small" label="SQL (1 column, e.g. SELECT DISTINCT labels['host_name'] FROM metrics)"
                value={v.query || ''}
                onChange={(e) => update(i, { query: e.target.value })}
                sx={{ flex: 2 }}
                multiline minRows={1}
                helperText="Can reference variables defined above it, e.g. … WHERE environment = $environment (cascading)."
              />
            )}
            <Tooltip title="Remove variable">
              <IconButton size="small" onClick={() => remove(i)} sx={{ alignSelf: 'center' }}>
                ✕
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * Pure helper — replace $name / ${name} placeholders client-side. The
 * backend does authoritative substitution; this is just used for the
 * preview "as it will run" SQL shown in the editor.
 */
export const previewSubstitute = (sql, variables, values) => {
  if (!sql) return sql;
  return sql.replace(/\$\{(\w+)\}|\$(\w+)/g, (m, a, b) => {
    const name = a || b;
    if (!name || name.startsWith('__')) return m;
    if (!variables?.find(v => v.name === name)) return m;
    const raw = values?.[name];
    if (raw == null || raw === '') return m;
    if (Array.isArray(raw)) return raw.map(s => `'${String(s).replace(/'/g, "''")}'`).join(',');
    if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
    return `'${String(raw).replace(/'/g, "''")}'`;
  });
};
